import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from '@/components/Login';
import RoutClientLogin from '@/components/RoutClientLogin';
import TeamLogin from '@/components/TeamLogin';
import PartnersLogin from '@/components/PartnersLogin';
import TeamHub from '@/components/team/TeamHub';
import WorkspaceSelector from '@/components/WorkspaceSelector';
import AdminDashboard from '@/components/AdminDashboard';
import FieldPortal from '@/components/FieldPortal';
import ComingSoon from '@/components/ComingSoon';
import OperationsPortal from '@/components/operations/OperationsPortal';
import ClientPortal from '@/components/client/ClientPortal';
import TfsAdminHub from '@/components/tfs/TfsAdminHub';
import TfsOperationsHub from '@/components/tfs/TfsOperationsHub';
import ContractBudgetsHub from '@/components/contractbudgets/ContractBudgetsHub';
import PayrollHub from '@/components/payroll/PayrollHub';
import PersonnelHub from '@/components/personnel/PersonnelHub';
import CalendarModule from '@/components/calendar/CalendarModule';
import SelfProfile from '@/components/admin/SelfProfile';
import PublicFeedbackForm from '@/components/PublicFeedbackForm';
import WorkOrderFeedback from '@/components/public/WorkOrderFeedback';
import PevCustomerReview from '@/components/operations/hub/PevCustomerReview';
import PreApplicationForm from '@/components/public/PreApplicationForm';
import ProviderOnboarding from '@/components/public/ProviderOnboarding';
import MobileUIKit from '@/components/common/mobile/MobileUIKit';
import { MobileEntranceGate } from '@/components/TFSConnect';
import ForcePasswordChange from '@/components/ForcePasswordChange';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import InactivityGuard from '@/components/InactivityGuard';
import { modulePolicyFor } from '@/lib/sessionPolicy';

// Tag every API request with the current module's inactivity policy so the
// backend only enforces the 20-min timeout on administrative modules.
axios.interceptors.request.use((config) => {
  try { config.headers['X-TFS-Policy'] = modulePolicyFor(window.location.pathname); } catch { /* ignore */ }
  return config;
});
import { WS, hasCap, defaultRouteFor } from '@/lib/workspaces';
import { captureEntranceParam, getEntrance, entranceLandingPath, postLogoutPath } from '@/lib/entrance';
import nativeMonitor from '@/native/serviceMonitor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Resolve any ?e=team|client|app preview-entrance override synchronously at
// module load — BEFORE React renders — so the very first paint of "/" already
// knows which door it is (a useEffect runs after child <Navigate> effects and
// would lose the query param on desktop).
captureEntranceParam();


// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('tfs_token'));
  const [loading, setLoading] = useState(true);

  // Persistent per-device id (independent sessions per device) + inactivity
  // sign-out message surfaced on the login screen.
  useEffect(() => {
    let did = localStorage.getItem('te_device_id');
    if (!did) {
      did = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('te_device_id', did);
    }
    axios.defaults.headers.common['X-Device-Id'] = did;
    try {
      const msg = sessionStorage.getItem('te_logout_msg');
      if (msg && !localStorage.getItem('tfs_token')) { toast.error(msg); sessionStorage.removeItem('te_logout_msg'); }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('tfs_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('tfs_token');
    localStorage.removeItem('tfs_admin_token');
    localStorage.removeItem('tfs_impersonation');
    // Clear per-session navigation + unsaved-draft state (never touches saved records).
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith('tfs_sess_') || k.startsWith('tfs_draft_')) sessionStorage.removeItem(k);
    });
    setToken(null);
    setUser(null);
  };

  // ---- View-as-User (secure admin impersonation) ----
  const [impersonation, setImpersonation] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tfs_impersonation') || 'null'); }
    catch { return null; }
  });

  // Start: preserve the admin's own token, switch to the target-scoped token,
  // then reload so the whole app boots in the target's context.
  const startImpersonation = (impToken, meta) => {
    const adminToken = localStorage.getItem('tfs_token');
    if (adminToken) localStorage.setItem('tfs_admin_token', adminToken);
    localStorage.setItem('tfs_impersonation', JSON.stringify(meta));
    localStorage.setItem('tfs_token', impToken);
    window.location.assign('/');
  };

  // End: stamp the audit (best-effort) then restore the admin's original token.
  const endImpersonation = async () => {
    const impToken = localStorage.getItem('tfs_token');
    try {
      await axios.post(`${API}/admin/impersonation/end`, {}, {
        headers: { Authorization: `Bearer ${impToken}` },
      });
    } catch (_) { /* non-fatal: restore regardless */ }
    const adminToken = localStorage.getItem('tfs_admin_token');
    localStorage.removeItem('tfs_impersonation');
    localStorage.removeItem('tfs_admin_token');
    if (adminToken) localStorage.setItem('tfs_token', adminToken);
    else localStorage.removeItem('tfs_token');
    window.location.assign('/tfs-admin?tab=capabilities');
  };

  // Session invalidation: any 401 from an authenticated API call (deactivated,
  // purged, password/role/capability change → session_version bump, or expiry)
  // clears auth and returns the user to login immediately.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        const isAuthCall = url.includes('/auth/login');
        if (status === 401 && !isAuthCall && localStorage.getItem('tfs_token')) {
          localStorage.removeItem('tfs_token');
          setToken(null);
          setUser(null);
          const dest = getEntrance() ? postLogoutPath() : '/login';
          if (window.location.pathname !== dest) window.location.assign(dest);
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const getDefaultRoute = () => defaultRouteFor(user);

  // Dedicated-entrance root. Each door (team/client/app) shows only its own
  // login + branding — never a public chooser. Preview uses a persisted `?e=`
  // override until the subdomains are activated; the bare domain keeps the
  // existing public mobile entrance for backward compatibility.
  const RootEntrance = () => {
    const e = getEntrance();
    if (user) return <Navigate to={entranceLandingPath(user, e)} replace />;
    if (e === 'team') return <TeamLogin />;
    if (e === 'client') return <RoutClientLogin />;
    if (e === 'app') return <Login />;
    return <MobileEntranceGate user={user} defaultRoute={getDefaultRoute()} />;
  };

  // Installed app only: if a service visit was active before a WebView reload,
  // re-attach native background monitoring with the current session token.
  useEffect(() => {
    if (user && token && typeof window !== 'undefined' && window.TFSServiceMonitor) {
      nativeMonitor.resume(token);
    }
  }, [user, token]);

  // Capability-gated route guard. Unauthorized access → safe redirect to the
  // user's default landing (their selector or single authorized workspace).
  const ProtectedRoute = ({ children, requiredCap = null }) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      );
    }
    if (!user) {
      return <Navigate to={postLogoutPath()} replace />;
    }
    // Impersonation ("View as User") is a read-only viewing session for the
    // admin — never divert it into the target's forced password-change screen.
    if (!impersonation && user.must_change_password && window.location.pathname !== '/force-password-change') {
      return <Navigate to="/force-password-change" replace />;
    }
    if (requiredCap) {
      const caps = Array.isArray(requiredCap) ? requiredCap : [requiredCap];
      if (!caps.some((c) => hasCap(user, c))) {
        return <Navigate to={getDefaultRoute()} replace />;
      }
    }
    return children;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, impersonation, startImpersonation, endImpersonation }}>
      {user && token && <InactivityGuard />}
      {user && impersonation && (
        <div
          data-testid="impersonation-banner"
          className="fixed inset-x-0 top-0 z-[9999] flex items-center justify-center gap-4 bg-[#8FC93A] px-4 py-2.5 text-[#0E2A47] shadow-lg"
        >
          <span className="text-sm font-bold" data-testid="impersonation-banner-label">
            Preview — Viewing as {impersonation.target_name} · read-only · you are not using your Global Administrator account
          </span>
          <button
            data-testid="end-impersonation-btn"
            onClick={endImpersonation}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0E2A47] px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#16324F]"
          >
            Exit Preview — Return to Global Administrator
          </button>
        </div>
      )}
      <div className={user && impersonation ? 'pt-11' : ''}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to={getDefaultRoute()} replace /> : <Login />}
          />
          <Route
            path="/rout"
            element={user ? <Navigate to="/client" replace /> : <RoutClientLogin />}
          />
          <Route
            path="/team"
            element={user ? <Navigate to="/team-hub" replace /> : <TeamLogin />}
          />
          <Route
            path="/partners"
            element={user ? <Navigate to={getDefaultRoute()} replace /> : <PartnersLogin />}
          />
          <Route path="/feedback" element={<PublicFeedbackForm />} />
          <Route path="/feedback/:token" element={<WorkOrderFeedback />} />
          <Route path="/pev-review/:iid" element={<PevCustomerReview />} />
          <Route path="/apply" element={<PreApplicationForm />} />
          <Route path="/provider-onboarding/:token" element={<ProviderOnboarding />} />
          <Route path="/mobile-ui-kit" element={<MobileUIKit />} />

          {/* Forced password change (temporary-password lifecycle). Never shown
              during an impersonation session — the admin is only viewing. */}
          <Route path="/force-password-change"
            element={user ? (impersonation ? <Navigate to={getDefaultRoute()} replace /> : <ForcePasswordChange />) : <Navigate to="/login" replace />} />

          {/* Five-entry Workspace Selector */}
          <Route
            path="/select-portal"
            element={
              <ProtectedRoute>
                <WorkspaceSelector />
              </ProtectedRoute>
            }
          />

          {/* TEAM worker app (worker-facing shell; section access enforced server-side) */}
          <Route path="/team-hub" element={<ProtectedRoute><TeamHub /></ProtectedRoute>} />
          <Route path="/team-hub/:section" element={<ProtectedRoute><TeamHub /></ProtectedRoute>} />

          {/* 1. ROUT Administration — Work Order System Control */}
          <Route
            path="/rout-admin/*"
            element={
              <ProtectedRoute requiredCap={WS.ROUT_ADMIN}>
                <OperationsPortal />
              </ProtectedRoute>
            }
          />
          {/* Compatibility: legacy /operations bookmarks */}
          <Route path="/operations/*" element={<Navigate to="/rout-admin" replace />} />

          {/* 2. ROUT Client Portal — Client Requests & Service Visibility */}
          <Route
            path="/client/*"
            element={
              <ProtectedRoute requiredCap={WS.ROUT_CLIENT}>
                <ClientPortal />
              </ProtectedRoute>
            }
          />

          {/* 3. TFS Administration Hub — Company & System Administration */}
          <Route
            path="/tfs-admin/*"
            element={
              <ProtectedRoute requiredCap={WS.TFS_ADMIN}>
                <TfsAdminHub />
              </ProtectedRoute>
            }
          />

          {/* 4. TFS Operations Hub — Operational Oversight & Field Control */}
          <Route
            path="/tfs-ops/*"
            element={
              <ProtectedRoute requiredCap={WS.TFS_OPS}>
                <TfsOperationsHub />
              </ProtectedRoute>
            }
          />

          {/* Payroll — Gross-Pay Preparation & Owner Approval */}
          <Route path="/payroll/*" element={<ProtectedRoute requiredCap={WS.PAYROLL}><PayrollHub /></ProtectedRoute>} />
          <Route path="/personnel-hub" element={<ProtectedRoute requiredCap={[WS.TFS_ADMIN, WS.TFS_OPS]}><PersonnelHub /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><CalendarModule /></ProtectedRoute>} />
          <Route path="/contract-budgets/*" element={<ProtectedRoute requiredCap={[WS.CONTRACT_BUDGETS, 'tagwork_budgets.view', 'tagwork_budgets.manage', 'tagwork_budgets.approve', 'tagwork_budgets.report']}><ContractBudgetsHub /></ProtectedRoute>} />

          {/* 5. TFS Field Portal — Assigned Work & Field Reporting */}
          <Route
            path="/field/*"
            element={
              <ProtectedRoute requiredCap={WS.FIELD}>
                <FieldPortal />
              </ProtectedRoute>
            }
          />

          {/* Legacy Administration / operational module screens — reachable by TFS Admin OR TFS Ops. NOT a selector entry. */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredCap={[WS.TFS_ADMIN, WS.TFS_OPS]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <SelfProfile />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<RootEntrance />} />
          <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
        </Routes>
      </BrowserRouter>
      </div>
      <Toaster position="top-right" />
    </AuthContext.Provider>
  );
}

export default App;
